import React, { useEffect, useState } from 'react';
import {
    StyledCompanyQuestionContainer,
    StyledTopSection,
    StyledMiddleSection,
    StyledBottomSection,
    StyledButton,
    StyledQuestionText,
    StyledSubtitleText,
} from './CompanyQuestion.styles';
import { useTranslation } from 'react-i18next';

interface Props<
    TAnswer = unknown,
    // Props specific to the AnswerComponent, excluding onSelectionChange
    TAnswerProps = Record<string, unknown>,
> {
    question: string;
    subtitle: string;
    // AnswerComponent must accept its own props (TAnswerProps) and our onSelectionChange
    AnswerComponent: React.ComponentType<
        TAnswerProps & {
            value: TAnswer | undefined;
            onSelectionChange: (value: TAnswer) => void;
        }
    >;
    answerProps?: TAnswerProps; // User provides specific props for AnswerComponent here
    onAnswer: (answer: TAnswer | undefined) => void; // currentAnswer can be undefined
    onSkip: () => void;
    initialAnswer?: TAnswer | undefined; // Added to allow initializing the answer
    disableSkip?: boolean;
}

const CompanyQuestion = <
    TAnswer = unknown,
    TAnswerProps = Record<string, unknown>,
>({
    question,
    subtitle,
    AnswerComponent,
    answerProps, // Defaulting is not handled here; {} can be passed by caller if needed
    onAnswer,
    onSkip,
    initialAnswer, // Added prop
    disableSkip = false,
}: Props<TAnswer, TAnswerProps>): React.ReactElement => {
    const { t } = useTranslation();
    const [currentAnswer, setCurrentAnswer] = useState<TAnswer | undefined>(
        initialAnswer
    );

    useEffect(() => {
        setCurrentAnswer(initialAnswer);
    }, [initialAnswer]);

    const handleSelectionChange = (value: TAnswer) => {
        // This value comes from AnswerComponent's onSelectionChange, typed as (value: TAnswer) => void
        setCurrentAnswer(value);
    };

    const handleAnswerClick = () => {
        // currentAnswer is TAnswer | undefined
        onAnswer(currentAnswer);
        setCurrentAnswer(undefined);
    };

    return (
        <StyledCompanyQuestionContainer>
            <StyledTopSection>
                <StyledQuestionText variant="h4" gutterBottom>
                    {question}
                </StyledQuestionText>
                <StyledSubtitleText variant="subtitle1">
                    {subtitle}
                </StyledSubtitleText>
            </StyledTopSection>

            <StyledMiddleSection>
                <AnswerComponent
                    {...(answerProps as TAnswerProps)} // Spread user-defined props
                    value={currentAnswer} // Pass currentAnswer as the value prop
                    onSelectionChange={handleSelectionChange} // Provide our selection handler
                />
            </StyledMiddleSection>

            <StyledBottomSection>
                <StyledButton
                    variant="outlined"
                    onClick={onSkip}
                    disabled={disableSkip}
                >
                    {t('tools.companyDetails.skip')}
                </StyledButton>
                <StyledButton
                    variant="contained"
                    onClick={handleAnswerClick}
                    disabled={currentAnswer === undefined}
                >
                    {t('tools.companyDetails.answer')}
                </StyledButton>
            </StyledBottomSection>
        </StyledCompanyQuestionContainer>
    );
};

export default CompanyQuestion;
